import React from 'react';
import { makeStyles } from '@mui/styles';
import { Paper, InputBase, IconButton, Button, Typography, TextField, Popover } from '@mui/material';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    width: 'auto',
  },
  searchFilterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  searchCard: {
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    maxWidth: 300,
    height: 40,
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 8,
    boxShadow: 'none',
  },
  inputBase: {
    flex: 1,
    width: theme.spacing(100),
    marginLeft: 0,
    paddingLeft: 8,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  iconButton: {
    padding: 0,
    marginLeft: 0,
    color: 'rgba(0, 0, 0, 0.56)',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  filterButton: {
    marginLeft: 8,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.admin.main,
    fontWeight: 700,
    height: 36,
    '& .MuiSvgIcon-root': {
      marginRight: 8,
    },
  },
  filterMenu: {
    padding: theme.spacing(2),
    width: 300,
  },
  filterMenuTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    '& > button': {
      marginLeft: theme.spacing(1),
    },
  },
  dateField: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}));

const PatientCustomQuestionsSearchFilter = ({
  searchTerm,
  handleSearchChange,
  handleFilterClick,
  hasActiveFilters,
  filterId,
  isFilterOpen,
  filterAnchorEl,
  handleFilterClose,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  handleFilterReset,
  handleFilterApply,
  theme,
}) => {
  const classes = useStyles();

  // Function to format date to MM/DD/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  // Create a formatted date range string for the chip display
  const formattedDateRange = React.useMemo(() => {
    if (hasActiveFilters && startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return '';
  }, [hasActiveFilters, startDate, endDate]);

  return (
    <div className={classes.container}>
      <div className={classes.searchFilterContainer}>
        <Paper
          className={classes.searchCard}
          elevation={0}
        >
          <IconButton
            type="submit"
            className={classes.iconButton}
            aria-label="search"
            size="large"
            sx={{ color: 'rgba(0, 0, 0, 0.56)' }}
          >
            <Search />
          </IconButton>
          <InputBase
            value={searchTerm}
            onChange={handleSearchChange}
            className={classes.inputBase}
            placeholder="Search questions"
            inputProps={{
              'aria-label': 'search questions',
              style: {
                '::placeholder': {
                  color: 'rgba(0, 0, 0, 0.60)',
                  opacity: 1,
                },
              },
            }}
            sx={{
              '& input::placeholder': {
                color: 'rgba(0, 0, 0, 0.60)',
                opacity: 1,
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(0, 0, 0, 0.60)',
                opacity: 1,
              },
            }}
          />
        </Paper>

        <Button
          variant="text"
          className={classes.filterButton}
          onClick={handleFilterClick}
          aria-describedby={filterId}
          color="inherit"
          sx={{
            color: hasActiveFilters ? theme.palette.primary.main : theme.palette.admin.main,
          }}
        >
          <FilterList />
          FILTERS
        </Button>

        <Popover
          id={filterId}
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <div className={classes.filterMenu}>
            <Typography className={classes.filterMenuTitle}>Filter by Date</Typography>

            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={classes.dateField}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={classes.dateField}
              InputLabelProps={{
                shrink: true,
              }}
            />

            <div className={classes.filterActions}>
              <Button
                onClick={handleFilterReset}
                color="inherit"
              >
                Reset
              </Button>
              <Button
                onClick={handleFilterApply}
                color="primary"
                variant="contained"
              >
                Apply
              </Button>
            </div>
          </div>
        </Popover>
      </div>
    </div>
  );
};

export default PatientCustomQuestionsSearchFilter;
